import type { CSSProperties } from 'react';

/**
 * Shared surface tokens for the client app.
 *
 * The client dashboard established this language first (frosted cards over a
 * slate gradient, indigo accent, 20px radius). Everything else in the client
 * area pulls from here so the app reads as one product rather than one
 * redesigned page plus a pile of default Ant Design cards.
 */
export const SURFACE = {
  /** One radius scale for the whole client area. */
  radius: {
    card: 20,
    cardMobile: 16,
    panel: 14,
    control: 10,
  },
  text: {
    strong: '#0f172a',
    body: '#334155',
    muted: '#64748b',
    faint: '#94a3b8',
  },
  line: {
    hair: '#e2e8f0',
    soft: '#f1f5f9',
  },
  /** The single accent. Used for the identity mark and active affordances. */
  accent: {
    from: '#667eea',
    to: '#764ba2',
    solid: '#5b6ee1',
  },
  page: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
} as const;

/** Frosted card shell. The primary surface of the client area. */
export const glassSurface = (isMobile: boolean): CSSProperties => ({
  background: 'rgba(255, 255, 255, 0.8)',
  backdropFilter: 'blur(20px)',
  borderRadius: isMobile ? SURFACE.radius.cardMobile : SURFACE.radius.card,
  border: '1px solid rgba(255, 255, 255, 0.5)',
  boxShadow: '0 4px 24px rgba(15, 23, 42, 0.06)',
  padding: isMobile ? 14 : 24,
  height: '100%',
  animation: 'sectionFadeUp 0.5s ease-out both',
});

/**
 * Quiet inset panel for grouping fields inside a card. Flat rather than
 * elevated: nesting a shadowed card inside a shadowed card reads as a bug.
 */
export const panelSurface = (isMobile: boolean): CSSProperties => ({
  background: '#ffffff',
  border: `1px solid ${SURFACE.line.hair}`,
  borderRadius: SURFACE.radius.panel,
  padding: isMobile ? 16 : 24,
});

/** The gradient mark used for identity avatars and section icons. */
export const accentMark = (size: number): CSSProperties => ({
  width: size,
  height: size,
  borderRadius: size / 2.6,
  background: `linear-gradient(135deg, ${SURFACE.accent.from} 0%, ${SURFACE.accent.to} 100%)`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#ffffff',
  flexShrink: 0,
});
